import { useEffect } from "react";
import "./App.css";
import { Toaster } from "@/components/ui/toaster";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClientInstance } from "@/lib/query-client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import { pagesConfig, ADMIN_PAGES } from "./pages.config.js";
import PageNotFound from "./lib/PageNotFound";
import { useAuth } from "./Authcontext.jsx";
import HomePage from "./pages/Home.jsx";
import CustomPage from "./pages/CustomPage.jsx";
import { SiteTextProvider } from "./cms/useSiteText.jsx";
import exquraWordmark from "./assets/branding/exqura-wordmark.png";

const { Pages, Layout } = pagesConfig;

const PUBLIC_PAGES = [
  "Login",
  "Home",
  "AboutUs",
  "ContactUs",
  "about-courses",
  "package-detail",
  "package-checkout",
  "golf-getaways",
  "exqura-experience",
  "privacy-policy",
  "data-deletion",
  "terms-of-service",
  "help-center",
  "faqs",
  "cancellation-policy",
  "careers",
  "experience-detail",
  "auth/singpass/callback",
  "auth/singpass/callback-popup"
];

// Where admins land after login / whenever they hit a non-admin page.
// Defaults to the first entry in ADMIN_PAGES.
const ADMIN_HOME = `/${ADMIN_PAGES[0]}`;

const isAdminPage = (name) => ADMIN_PAGES.includes(name);

const LayoutWrapper = ({ children, currentPageName }) =>
  Layout ? (
    <Layout currentPageName={currentPageName}>{children}</Layout>
  ) : (
    children
  );

// React Router doesn't reset scroll on navigation like a normal page load
// does — without this, going to a new page keeps whatever scroll position
// the previous page was left at.
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Full screen spinner while Firebase resolves session
function SplashScreen() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "#1e3d2e" }}
    >
      <div className="flex flex-col items-center gap-5">
        <img src={exquraWordmark} alt="Exqura" className="h-8 w-auto" />
        <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );
}

// Home page ("/") - admins never see the normal homepage, they get bounced to their area
function HomeRoute() {
  const { profile, loading } = useAuth();

  if (loading) return <SplashScreen />;
  if (profile?.role === "admin") return <Navigate to={ADMIN_HOME} replace />;

  return <HomePage />;
}

// Block protected pages until auth resolves + gate by role in both directions:
// non-admins can't reach admin pages, and admins can't reach non-admin pages.
function ProtectedRoute({ name, Page }) {
  const { user, profile, loading } = useAuth();

  if (loading) return <SplashScreen />;
  if (!user) return <Navigate to="/Login" replace />;

  const isAdmin = profile?.role === "admin";
  const pageIsAdminOnly = isAdminPage(name);

  if (pageIsAdminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  if (!pageIsAdminOnly && isAdmin) {
    return <Navigate to={ADMIN_HOME} replace />;
  }

  return (
    <LayoutWrapper currentPageName={name}>
      <Page />
    </LayoutWrapper>
  );
}

// Redirect logged-in users away from Login (but not from other public pages like AboutUs)
// Admins get sent to their own area instead of "/", and can't browse public marketing pages either.
function PublicRoute({ name, Page }) {
  const { user, profile, loading } = useAuth();

  if (loading) return <SplashScreen />;

  if (user) {
    const isAdmin = profile?.role === "admin";

    if (name === "Login") {
      return <Navigate to={isAdmin ? ADMIN_HOME : "/"} replace />;
    }
    if (isAdmin) {
      return <Navigate to={ADMIN_HOME} replace />;
    }
  }

  return (
    <LayoutWrapper currentPageName={name}>
      <Page />
    </LayoutWrapper>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClientInstance}>
      <SiteTextProvider>
        <Router>
          <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/Home" element={<Navigate to="/" replace />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          {/* Old URL, kept as a redirect for any existing links/bookmarks
              from before this page was renamed to About Courses. */}
          <Route
            path="/golf-booking"
            element={<Navigate to="/about-courses" replace />}
          />

          {/* Admin-created custom pages (Edit Site CMS "+ New Page") —
              the slug isn't known statically like the rest of `Pages`, so
              this is a single dynamic route rather than a per-page entry.
              PublicRoute handles the "admins get bounced to their area"
              behavior the same as every other marketing page. */}
          <Route
            path="/page/:slug"
            element={<PublicRoute name="custom-page" Page={CustomPage} />}
          />

          {Object.entries(Pages).map(([name, Page]) => (
            <Route
              key={name}
              path={`/${name}`}
              element={
                PUBLIC_PAGES.includes(name) ? (
                  <PublicRoute name={name} Page={Page} />
                ) : (
                  <ProtectedRoute name={name} Page={Page} />
                )
              }
            />
          ))}

          <Route path="*" element={<PageNotFound />} />
        </Routes>

        <Toaster />
        </Router>
      </SiteTextProvider>
    </QueryClientProvider>
  );
}

export default App;
